import { useState, useEffect } from 'react';
import { 
  AlertTriangle, X, Check, Copy
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';

export default function DuplicateOrderWarning({ 
  open, 
  onClose, 
  duplicateOrder,
  onProceed,
  onCancel 
}) {
  if (!duplicateOrder) return null;

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${Math.floor(diffHours / 24)} days ago`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-yellow-600">
            <AlertTriangle className="w-5 h-5" />
            Possible Duplicate Order
          </DialogTitle>
          <DialogDescription>
            A similar order was placed recently
          </DialogDescription>
        </DialogHeader>

        {/* Warning Message */}
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Copy className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
            <div>
              <p className="font-medium text-yellow-800">
                This order looks identical to a previous one
              </p>
              <p className="text-sm text-yellow-700 mt-1">
                Order placed {formatTimeAgo(duplicateOrder.created_at)} with the same items
              </p>
            </div>
          </div>
        </div>

        {/* Duplicate Order Details */}
        <div className="bg-slate-100 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="font-medium">Previous Order #{duplicateOrder.id?.slice(-6).toUpperCase()}</span>
            <span className="text-orange-500 font-bold">£{duplicateOrder.total?.toFixed(2)}</span>
          </div>
          
          <div className="space-y-1 text-sm text-slate-600">
            {duplicateOrder.items?.slice(0, 3).map((item, i) => (
              <div key={i} className="flex justify-between">
                <span>{item.quantity}x {item.name}</span>
                <span>£{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            {duplicateOrder.items?.length > 3 && (
              <p className="text-slate-400 text-xs">
                +{duplicateOrder.items.length - 3} more items
              </p>
            )}
          </div>

          {duplicateOrder.customer_name && (
            <div className="mt-2 pt-2 border-t border-slate-200 text-sm text-slate-500">
              Customer: {duplicateOrder.customer_name}
              {duplicateOrder.table_number && ` • Table ${duplicateOrder.table_number}`}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 mt-4">
          <Button 
            variant="outline" 
            onClick={onCancel}
            className="flex-1"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel Order
          </Button>
          <Button 
            onClick={onProceed}
            className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white"
            data-testid="proceed-duplicate"
          >
            <Check className="w-4 h-4 mr-2" />
            Proceed Anyway
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
